import { ReactNode } from "react";
import { Integration } from "@/types/v2";
import { createSubdomainUrl } from "@/lib/createSubdomainUrl";
import { useStore } from "@/stores/useStore";
import { useAnalyticsLocation } from "@/providers/AnalyticsLocationProvider";
import { analyticsRepository } from "@/repositories/analytics";

export interface ExternalLinkWrapperProps {
  children: ReactNode;
  cardId?: string;
}

export interface ExternalLinkWrapperLowLevelProps {
  children: ReactNode;
  shopId: string;
  subdomain?: string;
  cardId: string;
  integration: Integration;
  onLinkClick?: (analyticsData: {
    integration: string;
    cardId: string;
    location?: string;
    additionalData?: Record<string, any>;
  }) => void;
}

/**
 * Low-level external link wrapper that requires all props to be passed explicitly.
 * Use this when you need full control over the link generation.
 */
export const ExternalLinkWrapperLowLevel = ({
  children,
  shopId,
  subdomain,
  cardId,
  integration,
  onLinkClick,
}: ExternalLinkWrapperLowLevelProps) => {
  const analyticsLocation = useAnalyticsLocation();

  const href =
    integration.integration === "tekmetric"
      ? `${integration.linkUrl}/admin/shop/${shopId}/repair-orders/${cardId}/estimate`
      : integration.integration === "shopware" && subdomain
        ? `${createSubdomainUrl(subdomain, integration.linkUrl)}/work_orders/${cardId}`
        : "";

  const handleClick = () => {
    const analyticsData = {
      integration: integration.integration,
      cardId,
      location: analyticsLocation?.location,
      additionalData: analyticsLocation?.additionalData,
    };

    // Call custom callback if provided
    onLinkClick?.(analyticsData);

    // Default analytics tracking - you can replace this with your preferred analytics service
    analyticsRepository.logAnalyticsEvent("external_link_click", analyticsData);
  };

  return (
    <a href={href} rel="noreferrer" target="_blank" onClick={handleClick}>
      {children}
    </a>
  );
};

/**
 * High-level external link wrapper that uses the store to get shop and integration data.
 * Use this in most cases for convenience.
 */
export const ExternalLinkWrapper = ({
  children,
  cardId,
}: ExternalLinkWrapperProps) => {
  const { shop, integration } = useStore();

  if (
    !shop ||
    !integration ||
    (integration.integration === "shopware" && !shop.integrationSubdomain) ||
    !cardId
  ) {
    // If we don't have the necessary data, just return the children without a link
    return <>{children}</>;
  }

  return (
    <ExternalLinkWrapperLowLevel
      shopId={shop.shopId}
      subdomain={shop.integrationSubdomain}
      cardId={cardId}
      integration={integration}
    >
      {children}
    </ExternalLinkWrapperLowLevel>
  );
};
